import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { DynamicFormComponent } from 'app/_dynamic_form/containers/dynamic_form/dynamic-form.component';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-site-owner-users',
  templateUrl: './site-owners.component.html',
})
export class SiteOwnerUsersComponent implements OnInit {
  @ViewChild(DynamicFormComponent) filterForm: DynamicFormComponent;

  users: any[] = [];
  selectedUser: any;

  sidebarVisible = false;
  createUserModal = false;
  createUserForm: FormGroup;
  editUserForm: FormGroup;
  editUserModal = false;

  pageSizes: any = environment.pageSizes;
  pagination: any;
  @ViewChild('paginator', { static: true }) paginator;

  selectedSort: any;

  sortFields: any[] = [
    {
      sortName: 'First Name (asc)',
      sortString: 'skylarkUser.user.firstName,asc',
    },
    {
      sortName: 'First Name (desc)',
      sortString: 'skylarkUser.user.firstName,desc',
    },
    {
      sortName: 'Last Name (asc)',
      sortString: 'skylarkUser.user.lastName,asc',
    },
    {
      sortName: 'Last Name (desc)',
      sortString: 'skylarkUser.user.lastName,desc',
    },
  ];
  public showDisabledSiteOwners = false;
  public enabledOnlyUsers = [];

  role: any;
  routeRole: any;

  errorAlerts: any[] = [];

  constructor(
    public loader: LoadingBarService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public globals: Globals,
    public fb: FormBuilder,
    public notificationService: NotificationService,
    public siteOwnerUserService: SiteOwnerUserService
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;

    this.createUserForm = fb.group({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      login: new FormControl('', [Validators.required]),
      addAssets: new FormControl(true, [Validators.required]),
      allowCompulsoryCheckOnSiteForm: new FormControl(false, [Validators.required]),
      allowCompulsoryCheckOffSiteForm: new FormControl(false, [Validators.required]),
      allowWorkOrderScheduling: new FormControl(false, [Validators.required]),
      allowRestrictionsWorkflow: new FormControl(true, [Validators.required]),
      allowAssetScheduling: new FormControl(false, [Validators.required]),
      allowIncidentReporting: new FormControl(false, [Validators.required]),
      enabled: new FormControl(true, [Validators.required]),
      maxSiteCount: new FormControl(1, [Validators.required]),
      password: new FormControl('', [Validators.required]),
      rPassword: new FormControl('', [Validators.required]),
    });

    this.editUserForm = fb.group({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      login: new FormControl('', [Validators.required]),
      addAssets: new FormControl(false),
      allowCompulsoryCheckOnSiteForm: new FormControl(false, [Validators.required]),
      allowCompulsoryCheckOffSiteForm: new FormControl(false, [Validators.required]),
      allowWorkOrderScheduling: new FormControl(false, [Validators.required]),
      allowRestrictionsWorkflow: new FormControl(false, [Validators.required]),
      allowAssetScheduling: new FormControl(false, [Validators.required]),
      allowIncidentReporting: new FormControl(false, [Validators.required]),
      enabled: new FormControl(false),
      maxSiteCount: new FormControl(1, [Validators.required]),
    });

    this.selectedSort = this.sortFields[0];
  }

  ngOnInit() {
    this.resetPagination();
    this.getAllSiteOwners();
  }

  public getAllSiteOwners() {
    this.siteOwnerUserService.getAllSiteOwners(this.pagination, this.selectedSort.sortString).subscribe((data: any) => {
      this.users = data.body;
      this.pagination.totalRecords = data.headers.get('x-total-count');

      this.enabledOnlyUsers = this.users.filter(siteOwner => siteOwner.siteOwnerUser.enabled);
    });
  }

  /* PAGINATION */
  public resetPagination() {
    this.pagination = {
      size: this.pageSizes.slice(-1)[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getAllSiteOwners();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }
  /* PAGINATION END */

  // toggle the create user modal
  public toggleCreateUserModal() {
    this.createUserModal = !this.createUserModal;
  }

  // create user, reset forms, toggle modal, reload data
  public onCreateUserFormSubmit() {
    const values = this.createUserForm.value;
    if (values.password === values.rPassword) {
      const body = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        login: values.login,
        password: values.password,
        maxSiteCount: values.maxSiteCount,
        enabled: values.enabled,
        addAssets: values.addAssets,
        addSiteInduction: true,
        allowCompulsoryCheckOnSiteForm: values.allowCompulsoryCheckOnSiteForm,
        allowCompulsoryCheckOffSiteForm: values.allowCompulsoryCheckOffSiteForm,
        allowWorkOrderScheduling: values.allowWorkOrderScheduling,
        allowRestrictionsWorkflow: values.allowRestrictionsWorkflow,
        allowAssetScheduling: values.allowAssetScheduling,
        allowIncidentReporting: values.allowIncidentReporting,

      };
      this.siteOwnerUserService.createSiteOwner(body).subscribe((data: any) => {
        this.toggleCreateUserModal();
        this.createUserForm.reset();
        this.resetPagination();
        this.getAllSiteOwners();
        Swal.fire('Success', 'Site Owner Created!', 'success');
      });
    } else {
      this.errorAlerts.push({ severity: 'error', summary: 'Passwords do not match!' });
    }
  }

  // Handle onRowSelect of a user
  public async onRowSelect(event) {
    this.selectedUser = event.data;
    const { value: action } = await Swal.fire({
      titleText: event.data.firstName + ' ' + event.data.lastName,
      input: 'select',
      inputOptions: {
        edit: 'Edit',
        changePassword: 'Change Password',
        // 'delete': 'Delete'
      },
      inputPlaceholder: 'Select an option',
      showCancelButton: true,
      inputValidator: (value) => {
        return new Promise((resolve) => {
          resolve(null);
        });
      },
    });

    if (action) {
      if (action === 'edit') {
        this.editUser();
      } else if (action === 'changePassword') {
        this.changeUserPassword();
      } else {
        console.log('No valid option selected');
      }
    }
  }

  // Edit User
  public editUser() {
    this.editUserForm.patchValue({
      firstName: this.selectedUser.firstName,
      lastName: this.selectedUser.lastName,
      email: this.selectedUser.email,
      login: this.selectedUser.login,
      addAssets: this.selectedUser.siteOwnerUser.addAssets,
      enabled: this.selectedUser.siteOwnerUser.enabled,
      maxSiteCount: this.selectedUser.siteOwnerUser.maxSiteCount,
      allowCompulsoryCheckOnSiteForm: this.selectedUser.siteOwnerUser.allowCompulsoryCheckOnSiteForm,
      allowCompulsoryCheckOffSiteForm: this.selectedUser.siteOwnerUser.allowCompulsoryCheckOffSiteForm,
      allowWorkOrderScheduling: this.selectedUser.siteOwnerUser.allowWorkOrderScheduling,
      allowRestrictionsWorkflow: this.selectedUser.siteOwnerUser.allowRestrictionsWorkflow,
      allowAssetScheduling: this.selectedUser.siteOwnerUser.allowAssetScheduling,
      allowIncidentReporting: this.selectedUser.siteOwnerUser.allowIncidentReporting,
    });
    this.toggleEditUserModal();
  }

  public toggleEditUserModal() {
    this.editUserModal = !this.editUserModal;
  }

  public onEditUserFormSubmit() {
    const values = this.editUserForm.value;
    const body = {
      firstName: values.firstName,
      lastName: values.lastName,
      login: values.login,
      email: values.email,
      skylarkUserId: this.selectedUser.id,
      maxSiteCount: values.maxSiteCount,
      enabled: values.enabled,
      addAssets: values.addAssets,
      addSiteInduction: true,
      allowCompulsoryCheckOnSiteForm: values.allowCompulsoryCheckOnSiteForm,
      allowCompulsoryCheckOffSiteForm: values.allowCompulsoryCheckOffSiteForm,
      allowWorkOrderScheduling: values.allowWorkOrderScheduling,
      allowRestrictionsWorkflow: values.allowRestrictionsWorkflow,
      allowAssetScheduling: values.allowAssetScheduling,
      allowIncidentReporting: values.allowIncidentReporting,
    };
    this.siteOwnerUserService.editSiteOwner(body).subscribe((data: any) => {
      this.toggleEditUserModal();
      this.editUserForm.reset();
      this.resetPagination();
      this.selectedUser = null;
      this.getAllSiteOwners();
      Swal.fire('Success', 'Site Owner Edited!', 'success');
    });
  }

  // Change User Password
  public changeUserPassword() {
    (Swal as any)
      .mixin({
        input: 'text',
        confirmButtonText: 'Next &rarr;',
        showCancelButton: true,
        progressSteps: ['1', '2'],
      })
      .queue([
        {
          title: 'Enter New Password',
          text: 'Please enter a new password.',
        },
      ])
      .then((result) => {
        if (result.value) {
          Swal.fire({
            title: 'Confirm',
            text: 'Change Site Owner password to the following?',
            html: 'New Password: <pre><code>' + JSON.stringify(result.value[0]) + '</code></pre>',
            confirmButtonText: 'Confirm',
          }).then((res) => {
            if (res.value) {
              const body = {
                newPassword: result.value[0],
                skylarkUserId: this.selectedUser.id,
              };
              this.siteOwnerUserService.updateSiteOwnerPassword(body).subscribe((response) => {
                Swal.fire('Success', 'Site Owner Password Changed!', 'success');
              });
            }
          });
        }
      });
  }

  // Handle sort change and reload data
  // TO DO
  public sortChange(event) {
    this.selectedSort = event;
    this.goToFirstPage();
    this.getAllSiteOwners();
  }
}
